import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { API, BACKEND_URL } from '@/App';
import { Award, Trophy, Medal, ArrowLeft, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import ImageWithLoader from '@/components/ImageWithLoader';
import { useCache } from '@/context/CacheContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http') || path.startsWith('data:')) return path;
  return `${BACKEND_URL}${path}`;
};

export default function LeaderboardPage({ auth, onBack }) {
  const navigate = useNavigate();
  const { fetchOverallLeaderboard, fetchMonthlyLeaderboard } = useCache();
  const [overall, setOverall] = useState([]);
  const [monthlyBoards, setMonthlyBoards] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [availableMonths, setAvailableMonths] = useState([]);

  // Graph interaction state
  const [hoveredUser, setHoveredUser] = useState(null);
  const [clickedUser, setClickedUser] = useState(null);

  // Over Time Tab Data
  const [overTimeData, setOverTimeData] = useState(null);
  const [loadingOverTime, setLoadingOverTime] = useState(true);

  useEffect(() => {
    loadLeaderboards();
    loadOverTimeData();
  }, []);

  const loadLeaderboards = async () => {
    try {
      const leaderboard = await fetchOverallLeaderboard();
      setOverall(leaderboard);

      // Calculate available months (only Jan 2026 - Dec 2026 or current month)
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const months = [];
      const monthsData = {};

      // Only load 2026 months
      const endMonth = currentYear === 2026 ? currentMonth : 12;

      for (let month = 1; month <= endMonth; month++) {
        const monthKey = `2026-${month.toString().padStart(2, '0')}`;
        months.push(monthKey);

        try {
          const data = await fetchMonthlyLeaderboard(2026, month);
          monthsData[monthKey] = data;
        } catch (error) {
          console.error(`Failed to load leaderboard for ${monthKey}`, error);
        }
      }

      setAvailableMonths(months);
      setMonthlyBoards(monthsData);

      // Set to current month if in 2026, otherwise last month (Dec)
      if (currentYear === 2026 && currentMonth <= 12) {
        setCurrentMonthIndex(currentMonth - 1); // 0-indexed
      } else {
        setCurrentMonthIndex(months.length - 1);
      }
    } catch (error) {
      console.error('Failed to load leaderboards', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverTimeData = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard/over-time`, {
        headers: { Authorization: `Bearer ${auth.token}` }
      });
      setOverTimeData(response.data);
    } catch (error) {
      console.error("Failed to load over-time data", error);
    } finally {
      setLoadingOverTime(false);
    }
  };

  const handleUserClick = (userId) => {
    navigate(`/user/${userId}`);
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy className="text-amber-500" size={24} />;
    if (rank === 2) return <Medal className="text-gray-400" size={24} />;
    if (rank === 3) return <Medal className="text-amber-700" size={24} />;
    return <span className="text-gray-600 font-bold">{rank}</span>;
  };

  const LeaderboardCard = ({ user, rank, showMedals = false, clickable = false }) => {
    const Content = () => (
      <>
        <div className="w-10 flex justify-center">
          {getRankIcon(rank)}
        </div>
        <Avatar>
          {user.profile_picture ? (
            <ImageWithLoader
               src={getImageUrl(user.profile_picture)}
               alt={user.name}
               className="w-full h-full object-cover"
             />
          ) : (
            <AvatarFallback>{user.name[0]}{user.surname[0]}</AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1">
          <div className="font-semibold text-gray-900">{user.name} {user.surname}</div>
          <div className="text-sm text-gray-600">
            {user.completed_days} of {user.total_days_passed || user.total_days} days ({user.completion_percentage}%)
          </div>
        </div>
        {showMedals && user.medals_count > 0 && (
          <div className="flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-semibold">
            <Award size={16} />
            <span>{user.medals_count}</span>
          </div>
        )}
      </>
    );

    const className = `flex items-center gap-4 p-4 rounded-lg ${rank <= 3 ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-200' : 'bg-gray-50'} ${clickable ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`;

    if (clickable) {
      return (
        <Link
          to={`/user/${user.user_id}`}
          className={className}
          data-testid={`leaderboard-entry-${rank}`}
        >
          <Content />
        </Link>
      );
    }

    return (
      <div
        className={className}
        data-testid={`leaderboard-entry-${rank}`}
      >
        <Content />
      </div>
    );
  };

  const CustomLegend = ({ payload, sortedUsers }) => {
     if (!sortedUsers) return null;

     return (
        <div className="flex flex-col items-center mt-4 gap-2 relative z-10">
           <p className="text-sm text-gray-500 italic text-center mb-2">
               <span className="hidden md:inline">Click</span>
               <span className="md:hidden">Tap</span> on a user below to highlight their progress
           </p>

           <div
              className="flex flex-wrap justify-center gap-x-6 gap-y-3 px-2"
              onMouseLeave={() => {
                  if (!clickedUser) setHoveredUser(null);
              }}
           >
           {sortedUsers.map((user, index) => {
              const colors = [
                "#2563eb", "#db2777", "#18181b", "#16a34a", "#9333ea",
                "#ea580c","#0891b2", "#71717a", "#dc2626", "#4f46e5", "#be185d", "#059669",
                "#c026d3", "#ca8a04", "#1d4ed8"
              ];
              const color = colors[index % colors.length];

              const activeId = clickedUser || hoveredUser;
              const isActive = activeId === user.id;
              const isDimmed = activeId && !isActive;

              // Determine visual style helper
              let borderStyle = "solid";
              let dashArray = "0"; // solid
              if (index >= 5 && index < 10) {
                 borderStyle = "dotted";
                 dashArray = "2 2"; // SVG like
              } else if (index >= 10) {
                 borderStyle = "dashed";
                 dashArray = "6 3"; // SVG like
              }

              return (
                 <div
                    key={user.id}
                    className={`flex items-center gap-2 cursor-pointer transition-opacity duration-200 ${isDimmed ? 'opacity-40' : 'opacity-100'}`}
                    onMouseEnter={() => setHoveredUser(user.id)}
                    onClick={(e) => {
                        e.stopPropagation();
                        setClickedUser(user.id);
                    }}
                 >
                    <Avatar className="w-6 h-6 border border-gray-200">
                        {user.profile_picture ? (
                           <ImageWithLoader
                             src={getImageUrl(user.profile_picture)}
                             alt={user.name}
                             className="w-full h-full object-cover"
                           />
                        ) : (
                           <AvatarFallback className="text-[10px]">{user.name[0]}{user.surname[0]}</AvatarFallback>
                        )}
                    </Avatar>

                    {/* Visual Line Indicator */}
                    <div className="h-4 w-0 border-l-2"
                         style={{
                             borderColor: color,
                             borderLeftStyle: borderStyle === 'dotted' ? 'dotted' : borderStyle === 'dashed' ? 'dashed' : 'solid',
                             borderWidth: '2px'
                    }} />

                    <span className={`text-sm font-medium ${isActive ? 'font-bold' : ''}`} style={{ color: color }}>
                       {user.name}
                    </span>
                 </div>
              );
           })}
           </div>

            <Button
                variant="ghost"
                size="sm"
                className={`text-xs text-gray-500 h-6 mt-2 hover:bg-gray-100 transition-opacity ${!clickedUser && !hoveredUser ? 'opacity-50' : 'opacity-100'}`}
                disabled={!clickedUser && !hoveredUser}
                onClick={() => {
                    setClickedUser(null);
                    setHoveredUser(null);
                }}
            >
                Show All
            </Button>
        </div>
     );
  };

  const CustomDot = (props) => {
      const { cx, cy, stroke, payload, dataKey } = props;

      // Check for overlaps at this specific point (payload represents the x-axis entry)
      // Use stroke color for fill to avoid empty look and prevent re-coloring perception
      if (!overTimeData || !overTimeData.users) return <circle cx={cx} cy={cy} r={4} stroke={stroke} strokeWidth={2} fill={stroke} />;

      const currentValue = payload[dataKey];
      if (currentValue === null || currentValue === undefined) return null;

      // Find all users who share this exact value at this point
      const overlappingUsers = overTimeData.users
          .filter(u => payload[u.id] === currentValue)
          .sort((a, b) => a.id.localeCompare(b.id)); // Deterministic order

      if (overlappingUsers.length <= 1) {
          // Standard dot
          return <circle cx={cx} cy={cy} r={4} stroke={stroke} strokeWidth={2} fill={stroke} />;
      }

      // Overlap detected: Draw a wedge/slice
      const index = overlappingUsers.findIndex(u => u.id === dataKey);
      const total = overlappingUsers.length;

      const radius = 6;
      const startAngle = (index * 2 * Math.PI) / total;
      const endAngle = ((index + 1) * 2 * Math.PI) / total;

      // Helper to calculate X/Y on circle
      const getCoords = (angle) => ({
          x: cx + radius * Math.cos(angle - Math.PI / 2), // -PI/2 to start at top
          y: cy + radius * Math.sin(angle - Math.PI / 2)
      });

      const start = getCoords(startAngle);
      const end = getCoords(endAngle);

      const largeArcFlag = endAngle - startAngle <= Math.PI ? "0" : "1";

      const pathData = [
          `M ${cx} ${cy}`,
          `L ${start.x} ${start.y}`,
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`,
          "Z"
      ].join(" ");

      return <path d={pathData} fill={stroke} stroke="none" />;
  };

  const AnimatedActiveDot = (props) => {
      const { cx, cy, stroke, color } = props;

      return (
          <circle cx={cx} cy={cy} r={6} stroke="#fff" strokeWidth={2} fill={color} style={{ transformOrigin: 'center' }}>
              <animate attributeName="r" from="4" to="8" dur="0.2s" begin="0s" fill="freeze" calcMode="spline" keyTimes="0; 1" keySplines="0.4 0 0.2 1" />
          </circle>
      );
  };

  // Helper for line styles
  const getLineStyle = (index) => {
      if (index >= 5 && index < 10) return "3 3"; // Dotted
      if (index >= 10) return "10 5"; // Dashed
      return ""; // Solid
  };

  // Sort users if data exists to ensure consistent coloring and layering
  if (overTimeData && overTimeData.users) {
      // Sort by the value at the last date (latest)
      overTimeData.users.sort((a, b) => {
          const lastIndex = overTimeData.dates.length - 1;
          const valA = a.data[lastIndex] || 0;
          const valB = b.data[lastIndex] || 0;
          return valB - valA; // Descending
      });
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-teal-50 to-cyan-50 p-4 pb-24 relative z-0">
      <div className="max-w-2xl mx-auto relative z-0">
        <Card className="p-6 glass mb-6">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onBack}
              data-testid="back-button"
            >
              <ArrowLeft size={20} />
            </Button>
            <h1 className="text-3xl font-bold text-gray-800">Leaderboard</h1>
          </div>
        </Card>

        <Tabs defaultValue="monthly" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-6">
            <TabsTrigger value="overall" data-testid="tab-overall">Overall</TabsTrigger>
            <TabsTrigger value="monthly" data-testid="tab-monthly">By Month</TabsTrigger>
            <TabsTrigger value="time" data-testid="tab-time">Over Time</TabsTrigger>
          </TabsList>

          <TabsContent value="overall">
            <Card className="p-6 glass">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Trophy className="text-amber-500" />
                Overall Leaderboard
              </h2>
              <p className="text-sm text-gray-600 mb-4">Most challenges completed overall</p>
              <div className="space-y-3">
                {loading ? (
                   Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-gray-50/50">
                        <Skeleton className="w-8 h-8 rounded bg-gray-200" />
                        <Skeleton className="w-10 h-10 rounded-full bg-gray-200" />
                        <div className="flex-1 space-y-2">
                           <Skeleton className="h-4 w-32 bg-gray-200" />
                           <Skeleton className="h-3 w-24 bg-gray-200" />
                        </div>
                        <Skeleton className="h-6 w-16 rounded-full bg-gray-200" />
                      </div>
                   ))
                ) : (
                  overall.map((user) => {
                    const rank = overall.findIndex(u => u.completed_days === user.completed_days) + 1;
                    return <LeaderboardCard key={user.user_id} user={user} rank={rank} showMedals={true} clickable={true} />;
                  })
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="monthly">
            {(loading || (availableMonths.length > 0 && monthlyBoards[availableMonths[currentMonthIndex]])) && (
              <Card className="p-6 glass">
                <div className="relative flex items-center justify-center mb-4 min-h-[32px]">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentMonthIndex(Math.max(0, currentMonthIndex - 1))}
                      disabled={loading || currentMonthIndex === 0}
                      data-testid="prev-month-button"
                    >
                      ← Previous
                    </Button>
                  </div>
                  <h2 className="text-xl font-semibold text-center px-24 md:px-32">
                    {loading ? (
                       <Skeleton className="h-8 w-48 bg-gray-200 block mx-auto" />
                    ) : (
                       `${monthlyBoards[availableMonths[currentMonthIndex]].challenge_name} - ${format(new Date(availableMonths[currentMonthIndex] + '-01'), 'MMMM yyyy')}`
                    )}
                  </h2>
                  <div className="absolute right-0 top-1/2 -translate-y-1/2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentMonthIndex(Math.min(availableMonths.length - 1, currentMonthIndex + 1))}
                      disabled={loading || currentMonthIndex === availableMonths.length - 1}
                      data-testid="next-month-button"
                    >
                      Next →
                    </Button>
                  </div>
                </div>
                <div className="space-y-3 mt-4">
                  {loading ? (
                      Array.from({ length: 10 }).map((_, i) => (
                        <div key={i} className="flex items-center gap-4 p-4 rounded-lg bg-gray-50/50">
                          <Skeleton className="w-8 h-8 rounded bg-gray-200" />
                          <Skeleton className="w-10 h-10 rounded-full bg-gray-200" />
                          <div className="flex-1 space-y-2">
                             <Skeleton className="h-4 w-32 bg-gray-200" />
                             <Skeleton className="h-3 w-24 bg-gray-200" />
                          </div>
                        </div>
                     ))
                  ) : (
                     monthlyBoards[availableMonths[currentMonthIndex]].leaderboard.slice(0, 50).map((user) => {
                       const fullList = monthlyBoards[availableMonths[currentMonthIndex]].leaderboard;
                       const rank = fullList.findIndex(u => u.completed_days === user.completed_days) + 1;
                       return <LeaderboardCard key={user.user_id} user={user} rank={rank} clickable={true} />;
                    })
                  )}
                </div>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="time">
            <Card className="p-6 glass min-h-[400px]">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="text-teal-600" />
                    Top 15 Progress Over Time
                </h2>
                {loadingOverTime ? (
                    <div className="flex flex-col gap-4">
                        <Skeleton className="h-[300px] w-full bg-gray-200" />
                        <div className="flex gap-2 justify-center">
                            <Skeleton className="h-4 w-20 bg-gray-200" />
                            <Skeleton className="h-4 w-20 bg-gray-200" />
                            <Skeleton className="h-4 w-20 bg-gray-200" />
                        </div>
                    </div>
                ) : overTimeData && overTimeData.users && overTimeData.users.length > 0 ? (
                    <div className="flex flex-col items-center w-full">
                        <div className="h-[700px] w-full relative z-0">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                    data={overTimeData.dates.map((date, index) => {
                                        const point = { name: format(new Date(date), 'MMM d') };
                                        overTimeData.users.forEach(user => {
                                            point[user.id] = user.data[index] !== undefined ? user.data[index] : null;
                                        });
                                        return point;
                                    })}
                                    margin={{ top: 10, right: 30, left: 0, bottom: 50 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                    <XAxis
                                        dataKey="name"
                                        stroke="#6b7280"
                                        fontSize={12}
                                        tickMargin={10}
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis
                                        stroke="#6b7280"
                                        fontSize={12}
                                        unit="%"
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <Tooltip
                                        wrapperClassName="hidden md:block"
                                        wrapperStyle={{ zIndex: 1000 }}
                                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', zIndex: 1000 }}
                                        formatter={(value, name) => {
                                            const user = overTimeData.users.find(u => u.id === name);
                                            return [`${value}%`, user ? `${user.name} ${user.surname}`.trim() : name];
                                        }}
                                        itemSorter={(item) => {
                                            // Sort tooltip items by value (descending) or by user rank if constant
                                            const userIndex = overTimeData.users.findIndex(u => u.id === item.dataKey);
                                            return userIndex;
                                        }}
                                        labelStyle={{ color: '#1f2937', fontWeight: 'bold' }}
                                    />
                                    <Legend content={<CustomLegend sortedUsers={overTimeData.users} />} />
                                    {overTimeData.users.map((user, index) => {
                                        // Generate distinct colors
                                        const colors = [
                                            "#2563eb", "#db2777", "#18181b", "#16a34a", "#9333ea",
                                            "#ea580c","#0891b2", "#71717a", "#dc2626", "#4f46e5", "#be185d", "#059669",
                                            "#c026d3", "#ca8a04", "#1d4ed8"
                                        ];
                                        const color = colors[index % colors.length];

                                        const activeId = clickedUser || hoveredUser;
                                        const isActive = activeId === user.id;
                                        const isDimmed = activeId && !isActive;

                                        return (
                                            <Line
                                                key={user.id}
                                                type="monotone"
                                                dataKey={user.id}
                                                stroke={color}
                                                strokeWidth={isActive ? 4 : 2}
                                                strokeOpacity={isDimmed ? 0.2 : 1}
                                                strokeDasharray={getLineStyle(index)}
                                                dot={<CustomDot />}
                                                activeDot={<AnimatedActiveDot color={color} />}
                                                connectNulls
                                                animationDuration={1000}
                                            />
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center justify-center h-[300px] text-gray-500">
                        No data available yet for the graph.
                    </div>
                )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
